import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchRequestTypes } from '../api/Request.client';
import { queryKeys } from '../api/queryKeys';
import { IRequestType } from '../constants/interfaces';

// Define the context shape
interface RequestTypesContextType {
  requestTypes: IRequestType[];
  sortedRequestTypes: IRequestType[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Create the context with a default value
const RequestTypesContext = createContext<RequestTypesContextType>({
  error: null,
  loading: false,
  refetch: async () => Promise.resolve(),
  requestTypes: [],
  sortedRequestTypes: [],
});

// Custom hook to use the context
export const useRequestTypesContext = () => useContext(RequestTypesContext);

// Provider component
export const RequestTypesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    data = [],
    isLoading: loading,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    gcTime: 8 * 60 * 60 * 1000,
    queryFn: fetchRequestTypes,
    queryKey: queryKeys.requestTypes(),
    staleTime: Infinity,
  });

  const requestTypes = data as IRequestType[];
  const error = queryError
    ? 'Failed to load request types. Please try again later.'
    : null;

  const sortedRequestTypes = useMemo(
    () => [...requestTypes].sort((a, b) => a.name.localeCompare(b.name)),
    [requestTypes]
  );

  const refetch = async () => {
    await queryRefetch();
  };

  return (
    <RequestTypesContext.Provider
      value={{ error, loading, refetch, requestTypes, sortedRequestTypes }}
    >
      {children}
    </RequestTypesContext.Provider>
  );
};
